export const coverageObjective = 'Certificate lifecycle and failure handling in Azure App Service';

export const coverageCases = {
  hosting: {
    label: 'Known · Covered',
    title: 'Answering an existing question',
    explanation: 'The assistant retrieves relevant documentation to answer a question someone has asked.',
    outcome: 'The assistant can answer using what has already been collected.',
    drivesResearch: false,
  },
  binding: {
    label: 'Known · Missing',
    title: 'Researching an unanswered question',
    explanation: 'The current guidance does not explain how the application uses a renewed certificate. This question gives the next search a specific target.',
    outcome: 'Their question gives AI a reason to find supporting knowledge.',
    drivesResearch: true,
  },
  api: {
    label: 'Latent · Covered',
    title: 'Using knowledge for a new question',
    explanation: 'Documentation collected for Python web apps also covers REST APIs. The assistant can answer this new question using the same source.',
    outcome: 'AI can recognise that connection when the question comes up.',
    drivesResearch: false,
  },
  code: {
    label: 'Latent · Missing',
    title: 'Anticipating missing guidance',
    explanation: 'AI proposes a related question, checks whether the knowledge base can answer it, and adds any missing guidance to the research task.',
    outcome: 'When the knowledge base cannot answer it, that predicted gap helps guide what to research next.',
    drivesResearch: true,
  },
};

export const knowledgeGaps = [
  { id: 'renewal', origin: 'User question', text: 'Why did the certificate expire?', sourceId: 'certificates' },
  { id: 'binding', origin: 'User question', text: 'Why is the renewed certificate not being served?', sourceId: 'bindings' },
  { id: 'code', origin: 'AI prediction', text: 'Could renewal affect code that uses the certificate?', sourceId: 'certificate-code' },
];

export const managedSources = [
  {
    id: 'overview', title: 'App Service overview', url: 'https://learn.microsoft.com/en-us/azure/app-service/overview',
    objective: 'App Service hosting capabilities',
    currentContent: 'App Service hosts web applications and REST APIs. Supported application stacks include Python, Java, Node.js, .NET, and PHP.',
    chunks: [{ id: 'hosting', title: 'Supported application stacks' }, { id: 'api', title: 'Hosting REST APIs' }],
    authority: 'Microsoft Learn', contribution: 'Explains which applications the service can host.',
  },
  {
    id: 'certificates', title: 'Add and manage certificates', url: 'https://learn.microsoft.com/en-us/azure/app-service/configure-ssl-certificate',
    objective: coverageObjective,
    currentContent: 'Free App Service managed certificates renew automatically while their prerequisites remain satisfied. Certificates imported from Key Vault synchronise to App Service. Manually uploaded certificates follow a different renewal path.',
    chunks: [{ id: 'renewal-types', title: 'Renewal behaviour by certificate type' }, { id: 'renewal-conditions', title: 'Conditions for managed renewal' }],
    authority: 'Microsoft Learn', contribution: 'Distinguishes renewal paths and the conditions they depend on.',
  },
  {
    id: 'bindings', title: 'Secure a custom domain with a certificate', url: 'https://learn.microsoft.com/en-us/azure/app-service/configure-ssl-bindings',
    objective: coverageObjective,
    currentContent: 'A certificate must be bound to the custom domain. The TLS binding determines the certificate served for that domain. Certificate renewal and the active binding both need to be checked.',
    chunks: [{ id: 'domain-binding', title: 'Certificate-to-domain bindings' }, { id: 'binding-check', title: 'Checking the certificate in use' }],
    authority: 'Microsoft Learn', contribution: 'Connects the renewed certificate to the domain that serves it.',
  },
  {
    id: 'certificate-code', title: 'Use certificates in application code', url: 'https://learn.microsoft.com/en-us/azure/app-service/configure-ssl-certificate-in-code',
    objective: coverageObjective,
    currentContent: 'Application code loads certificates through certificate-loading settings and thumbprints. A renewed certificate can have a new thumbprint. Explicit references may need updating so the application can load the replacement.',
    chunks: [{ id: 'certificate-loading', title: 'Loading certificates in application code' }, { id: 'thumbprint-change', title: 'Thumbprints after renewal' }],
    authority: 'Microsoft Learn', contribution: 'Adds a related application failure beyond the original binding question.',
  },
];

export function sourceById(id) { return managedSources.find(source => source.id === id); }
export function normalizeSourceContent(content) { return String(content).replace(/\s+/g, ' ').trim(); }

export function initialRegistry() {
  return [
    { id: 'overview', acceptedSnapshot: sourceById('overview').currentContent, kbMappings: ['hosting', 'api'], objectives: ['App Service hosting capabilities'], lastChecked: 'Not checked in this run', status: 'Active' },
    { id: 'certificates', acceptedSnapshot: 'Free App Service managed certificates renew automatically.', kbMappings: ['renewal-basics'], objectives: [coverageObjective], lastChecked: 'Not checked in this run', status: 'Active' },
  ];
}

export function gapIsCovered(gapId, registry) {
  const gap = knowledgeGaps.find(item => item.id === gapId);
  const record = registry.find(item => item.id === gap?.sourceId);
  return !!record && normalizeSourceContent(record.acceptedSnapshot) === normalizeSourceContent(sourceById(record.id).currentContent);
}

export function commitSources(registry, sourceIds) {
  return sourceIds.reduce((records, id) => {
    const source = sourceById(id);
    if (!source) return records;
    const existing = records.find(item => item.id === id);
    const accepted = { id, acceptedSnapshot: source.currentContent, kbMappings: source.chunks.map(chunk => chunk.id), objectives: [...new Set([...(existing?.objectives || []), source.objective])], lastChecked: existing?.lastChecked || 'Awaiting first check', status: 'Active' };
    return [...records.filter(item => item.id !== id), accepted];
  }, [...registry]);
}

export function compareSource(record) {
  const source = sourceById(record.id);
  return { changed: normalizeSourceContent(record.acceptedSnapshot) !== normalizeSourceContent(source.currentContent), accepted: record.acceptedSnapshot, retrieved: source.currentContent };
}

export function assistantAnswer(question, registry) {
  if (question === 'hosting') return 'Yes. Azure App Service supports Python web applications alongside several other application stacks.';
  if (question === 'api') return 'Yes. App Service can host REST APIs as well as web applications. The existing hosting documentation already covers this use.';
  if (question === 'renewal' && gapIsCovered('renewal', registry)) return 'Start with the certificate type. Free managed certificates renew while their prerequisites remain satisfied. Key Vault imports and manually uploaded certificates have different renewal paths.';
  if (question === 'binding' && gapIsCovered('binding', registry)) return 'Check the certificate bound to the custom domain. A replacement certificate and the domain’s active TLS binding are separate things to verify.';
  if (question === 'code' && gapIsCovered('code', registry)) return 'Check how the application loads its certificate. Renewal can change the thumbprint, so explicit certificate references may need updating.';
  return '';
}
